import AtlasLaunch from "@/pages/AtlasLaunch";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import InternationalBuyerFinder from "@/pages/InternationalBuyerFinder";
import InternationalBuyersDashboard from "@/pages/InternationalBuyersDashboard";
import ResetPassword from "@/pages/ResetPassword";
import Activate from "@/pages/Activate";
import Dashboard from "@/pages/Dashboard";
import Properties from "@/pages/Properties";
import PropertyFeeds from "@/pages/PropertyFeeds";
import PropertyIntakes from "@/pages/PropertyIntakes";
import PropertyDetail from "@/pages/PropertyDetail";
import Places from "@/pages/Places";
import PlaceProfile from "@/pages/PlaceProfile";
import Contacts from "@/pages/Contacts";
import Requirements from "@/pages/Requirements";
import OpportunityFinder from "@/pages/OpportunityFinder";
import BankOpportunities from "@/pages/BankOpportunities";
import BankSources from "@/pages/BankSources";
import ConstructionPartners from "@/pages/ConstructionPartners";
import ConstructionDiscovery from "@/pages/ConstructionDiscovery";
import InvestorOwnership from "@/pages/InvestorOwnership";
import InvestorOwnershipDiscovery from "@/pages/InvestorOwnershipDiscovery";
import LegalPartners from "@/pages/LegalPartners";
import LegalPartnerDiscovery from "@/pages/LegalPartnerDiscovery";
import AgencyPartners from "@/pages/AgencyPartners";
import AgencyPartnerDiscovery from "@/pages/AgencyPartnerDiscovery";
import LegalDocuments from "@/pages/LegalDocuments";
import LegalTemplates from "@/pages/LegalTemplates";
import Pipeline from "@/pages/Pipeline";
import Tasks from "@/pages/Tasks";
import Reports from "@/pages/Reports";
import Messages from "@/pages/Messages";
import Settings from "@/pages/Settings";
import Users from "@/pages/Users";
import Approvals from "@/pages/Approvals";
import AIWorker from "@/pages/AIWorker";
import AIAgents from "@/pages/AIAgents";
import Portal from "@/pages/Portal";
import LegalPublicPage from "@/pages/LegalPublicPage";

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "investor") return <Navigate to="/portal" replace />;
  return children;
}

function PortalOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "investor" && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/privacy-policy" element={<LegalPublicPage type="privacy" />} />
          <Route path="/terms-of-service" element={<LegalPublicPage type="terms" />} />
          <Route path="/data-deletion" element={<LegalPublicPage type="deletion" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/international-buyers/:country?" element={<InternationalBuyerFinder />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/activate" element={<Activate />} />
          <Route path="/atlas" element={<Protected><AdminOnly><AtlasLaunch /></AdminOnly></Protected>} />
          <Route path="/portal" element={<PortalOnly><Portal /></PortalOnly>} />
          <Route
            path="/"
            element={
              <Protected>
                <Layout />
              </Protected>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="properties" element={<Properties />} />
            {process.env.REACT_APP_PROPERTY_FEEDS_ENABLED === "true" && <Route path="property-integrations" element={<AdminOnly><PropertyFeeds /></AdminOnly>} />}
            <Route path="property-intakes" element={<PropertyIntakes />} />
            <Route path="properties/:id" element={<PropertyDetail />} />
            <Route path="places" element={<Places />} />
            <Route path="places/:id" element={<PlaceProfile />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="international-buyers-dashboard" element={<InternationalBuyersDashboard />} />
            <Route path="requirements" element={<Requirements />} />
            <Route path="opportunity-finder" element={<OpportunityFinder />} />
            <Route path="bank-opportunities" element={<BankOpportunities />} />
            <Route path="construction-partners" element={<ConstructionPartners />} />
            <Route path="construction-partners/discover" element={<ConstructionDiscovery />} />
            <Route path="investor-ownership" element={<InvestorOwnership />} />
            <Route path="investor-ownership/discover" element={<InvestorOwnershipDiscovery />} />
            <Route path="legal-partners" element={<LegalPartners />} />
            <Route path="legal-partners/discover" element={<LegalPartnerDiscovery />} />
            <Route path="agency-partners" element={<AgencyPartners />} />
            <Route path="agency-partners/discover" element={<AgencyPartnerDiscovery />} />
            <Route path="bank-opportunities/sources" element={<BankSources />} />
            <Route path="bank-opportunities/:opportunityId/legal" element={<LegalDocuments />} />
            <Route path="bank-opportunities/legal-templates" element={<AdminOnly><LegalTemplates /></AdminOnly>} />
            <Route path="pipeline" element={<Pipeline />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="messages" element={<AdminOnly><Messages /></AdminOnly>} />
            <Route path="reports" element={<Reports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="users" element={<AdminOnly><Users /></AdminOnly>} />
            <Route path="approvals" element={<AdminOnly><Approvals /></AdminOnly>} />
            <Route path="ai-worker" element={<AdminOnly><AIWorker /></AdminOnly>} />
            <Route path="ai-agents" element={<AdminOnly><AIAgents /></AdminOnly>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
